"use client";

import { createContext, useContext, useState, useEffect } from "react";

interface MotionContextType {
  isMotionEnabled: boolean;
  toggleMotion: () => void;
}

const MotionContext = createContext<MotionContextType>({
  isMotionEnabled: true,
  toggleMotion: () => {},
});

export function MotionProvider({ children }: { children: React.ReactNode }) {
  // Default on so first paint is not blocked; sync from localStorage after mount (may briefly
  // disagree for returning visitors who disabled motion — acceptable vs hiding the whole tree).
  const [isMotionEnabled, setIsMotionEnabled] = useState(true);

  useEffect(() => {
    try {
      const savedMotion = localStorage.getItem("motion");
      if (savedMotion !== null) {
        setIsMotionEnabled(savedMotion === "true");
        return;
      }
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;
      setIsMotionEnabled(!prefersReducedMotion);
    } catch {
      // localStorage unavailable (private mode, etc.)
    }
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.motion = isMotionEnabled ? "on" : "off";
    root.classList.toggle("motion-disabled", !isMotionEnabled);
  }, [isMotionEnabled]);

  useEffect(() => {
    if (isMotionEnabled) return;

    // Enforce reduced motion even in pages/components that still mount autoplay videos.
    const stopVideo = (video: HTMLVideoElement) => {
      video.pause();
      try {
        video.currentTime = 0;
      } catch {
        // Ignore seek errors for media that cannot seek yet.
      }
    };

    const videos = document.querySelectorAll("video");
    videos.forEach((video) => stopVideo(video));
  }, [isMotionEnabled]);

  const toggleMotion = () => {
    const newValue = !isMotionEnabled;
    setIsMotionEnabled(newValue);
    try {
      localStorage.setItem("motion", String(newValue));
    } catch {
      // localStorage unavailable (private mode, etc.)
    }
  };

  return (
    <MotionContext.Provider value={{ isMotionEnabled, toggleMotion }}>
      {children}
    </MotionContext.Provider>
  );
}

export function useMotion() {
  const context = useContext(MotionContext);
  if (context === undefined) {
    throw new Error("useMotion must be used within a MotionProvider");
  }
  return context;
}
